import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { SUPPORTED_KINDS, getKindConfig, type TagType } from '@/lib/nip51';

export interface LinkItem {
  tag: TagType;
  value: string;
  label: string;
}

export interface LinkList {
  event: NostrEvent;
  id: string;
  kind: number;
  dTag: string;
  title: string;
  description: string;
  image: string;
  items: LinkItem[];
  hashtags: string[];
}



function parseLinkList(event: NostrEvent): LinkList | null {
  const kindConfig = getKindConfig(event.kind);
  if (!kindConfig) return null;

  // Standard (non-set) lists may not have a d-tag; use the kind as identifier
  const dTag = event.tags.find(([n]) => n === 'd')?.[1] ?? String(event.kind);

  const title = event.tags.find(([n]) => n === 'title')?.[1] ?? kindConfig.name;
  const description = event.tags.find(([n]) => n === 'description')?.[1] ?? '';
  const image = event.tags.find(([n]) => n === 'image')?.[1] ?? '';

  const items: LinkItem[] = [];

  // Parse supported tag types for this kind
  for (const [tagName, value, labelOrRelay] of event.tags) {
    if (!value) continue;

    if (tagName === 'r') {
      items.push({ tag: 'r', value, label: labelOrRelay ?? value });
    } else if (tagName === 'p') {
      items.push({ tag: 'p', value, label: labelOrRelay ?? value });
    } else if (tagName === 'e') {
      items.push({ tag: 'e', value, label: labelOrRelay ?? value });
    } else if (tagName === 'a') {
      items.push({ tag: 'a', value, label: labelOrRelay ?? value });
    } else if (tagName === 'relay') {
      items.push({ tag: 'relay', value, label: value });
    } else if (tagName === 'word') {
      items.push({ tag: 'word', value, label: value });
    }
  }

  const hashtags = event.tags
    .filter(([n]) => n === 't')
    .map(([, tag]) => tag)
    .filter(Boolean) as string[];

  return {
    event,
    id: event.id,
    kind: event.kind,
    dTag,
    title,
    description,
    image,
    items,
    hashtags,
  };
}

export function useLinkLists(pubkey: string | undefined) {
  const { nostr } = useNostr();

  return useQuery<LinkList[]>({
    queryKey: ['nostr', 'link-lists', pubkey ?? ''],
    queryFn: async (c) => {
      if (!pubkey) return [];

      const events = await nostr.query(
        [{ kinds: SUPPORTED_KINDS, authors: [pubkey], limit: 100 }],
        { signal: c.signal },
      );

      const lists: LinkList[] = [];
      for (const event of events) {
        const list = parseLinkList(event);
        if (list) lists.push(list);
      }

      // Sort by creation time, newest first
      lists.sort((a, b) => b.event.created_at - a.event.created_at);
      return lists;
    },
    enabled: !!pubkey,
  });
}

export function useLinkList(pubkey: string | undefined, dTagOrKind: string | undefined, kind?: number) {
  const { nostr } = useNostr();

  return useQuery<LinkList | null>({
    queryKey: ['nostr', 'link-list', pubkey ?? '', dTagOrKind ?? '', kind ?? 0],
    queryFn: async (c) => {
      if (!pubkey || !dTagOrKind) return null;

      const resolvedKind = kind ?? 30003;

      const events = await nostr.query(
        [{ kinds: [resolvedKind], authors: [pubkey], '#d': [dTagOrKind], limit: 1 }],
        { signal: c.signal },
      );

      if (events.length === 0) return null;
      return parseLinkList(events[0]);
    },
    enabled: !!pubkey && !!dTagOrKind,
  });
}

export { parseLinkList };
